import * as React from "react";
import { formatMonthChartAxis } from "../../helpers/DateTime";
import { chartTheme, demandColor } from "../../Theme";
import {
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryTheme,
} from "victory";
import { chartTooltipContainer } from "./ChartTooltipContainer";

interface ChartData {
  month: number; // unique across years
  year: number;
  value: number;
  projected: boolean;
}

export interface Props {
  height?: number;
  title: string;
  timeline: ChartData[];
  format: (n: number) => number | string;
}

const ChartFinances = (props: Props): React.JSX.Element => {
  // Figure out the boundaries of the chart data
  let domainMin = 0;
  let domainMax = 0;
  const rangeMin = props.timeline[0].month;
  const rangeMax = Math.max(
    rangeMin + 11,
    props.timeline[props.timeline.length - 1].month,
  );
  const past = [] as ChartData[];
  const projected = [] as ChartData[];
  props.timeline.forEach((d: ChartData) => {
    domainMin = Math.min(domainMin, d.value);
    domainMax = Math.max(domainMax, d.value);
    if (d.projected) {
      projected.push(d);
    } else {
      past.push(d);
    }
  });
  if (projected.length > 0 && past.length > 0) {
    projected.push(past[past.length - 1]);
  }
  const multiyear = rangeMax - rangeMin > 12;

  // Wrapping in spare div prevents excessive height bug
  return (
    <div>
      <VictoryChart
        theme={VictoryTheme.material}
        padding={{ top: 10, bottom: 25, left: 55, right: 5 }}
        domain={{ x: [rangeMin, rangeMax], y: [domainMin, domainMax] }}
        domainPadding={{ y: [6, 6] }}
        height={props.height || 300}
        containerComponent={chartTooltipContainer({
          ariaLabel: `Chart of ${props.title} over time`,
          labels: ({ datum }: { datum: ChartData }) =>
            props.format(datum.value).toString(),
        })}
      >
        <VictoryAxis
          tickFormat={(t: number) => formatMonthChartAxis(t, multiyear)}
          tickLabelComponent={<VictoryLabel dy={-5} />}
          style={{
            axis: chartTheme.axis,
            grid: {
              display: "none",
            },
            tickLabels: chartTheme.tickLabels,
          }}
        />
        <VictoryAxis
          dependentAxis
          tickFormat={props.format}
          tickLabelComponent={<VictoryLabel dx={5} />}
          fixLabelOverlap={true}
          style={{
            axis: chartTheme.axis,
            tickLabels: chartTheme.tickLabels,
          }}
        />
        <VictoryLine
          data={past}
          x="month"
          y="value"
          style={{
            data: {
              stroke: demandColor,
            },
          }}
        />
        <VictoryLine
          data={projected}
          x="month"
          y="value"
          style={{
            data: {
              stroke: demandColor,
              strokeDasharray: "4,4",
            },
          }}
        />
        <VictoryLabel textAnchor="middle" x={200} y={7} text={props.title} />
      </VictoryChart>
    </div>
  );
};
/**
 * The series only changes when a month rolls over or the player changes something, so memoising
 * lets the whole Victory subtree -- easily the most expensive render in the game -- be skipped
 * on the frames in between. Finances hands over a referentially stable series for exactly this.
 */
export default React.memo(ChartFinances);
